import React from 'react';
import { SearchBar } from '@/components/ui';
import { HistoryViewToggle, type HistoryView } from './HistoryViewToggle';
import { LoadMore } from './LoadMore';

// LR-498: the left rail of the History master–detail shell — the view toggle, a search box, and
// the active list (passed as children: DateGroupedList or CounterpartyList). Fixed width, its own
// vertical scroll; the right detail pane scrolls independently. The list is paged on the server:
// scrolling near its end (or the button under it) asks for the next page.

// How close to the bottom (px) the scroll has to get before the next page is requested.
const LOAD_AHEAD_PX = 200;

export interface CounterpartyRailProps {
  hview: HistoryView;
  onHviewChange: (value: HistoryView) => void;
  query: string;
  onQueryChange: (query: string) => void;
  resultCount?: number;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  /** The last page request failed: scrolling no longer retries it, the button does. */
  loadMoreFailed?: boolean;
  onLoadMore?: () => void;
  /** Label of the next-page button, e.g. "Load more runs". */
  loadMoreLabel?: string;
  /** The active list. */
  children: React.ReactNode;
}

export const CounterpartyRail: React.FC<CounterpartyRailProps> = ({
  hview,
  onHviewChange,
  query,
  onQueryChange,
  resultCount,
  hasMore,
  isLoadingMore,
  loadMoreFailed,
  onLoadMore,
  loadMoreLabel = 'Load more',
  children,
}) => {
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      hasMore &&
      !isLoadingMore &&
      !loadMoreFailed &&
      onLoadMore &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - LOAD_AHEAD_PX
    ) {
      onLoadMore();
    }
  };
  return (
    <div className="flex w-[374px] shrink-0 flex-col border-r border-lr-border">
      <div className="px-[18px] pb-2.5 pt-3">
        <HistoryViewToggle value={hview} onChange={onHviewChange} />
      </div>
      <div className="px-[18px] pb-[11px]">
        <SearchBar
          value={query}
          onChange={onQueryChange}
          placeholder="Search…"
          resultCount={resultCount}
          debounceMs={250}
        />
      </div>
      <div className="flex-1 overflow-y-auto border-t border-lr-border" onScroll={onScroll}>
        {children}
        <LoadMore
          hasMore={hasMore}
          isLoading={isLoadingMore}
          isError={loadMoreFailed}
          onLoadMore={onLoadMore}
          label={loadMoreLabel}
        />
      </div>
    </div>
  );
};
